@if (loading) {
  <div class="loading-overlay">
    <mat-spinner diameter="60"></mat-spinner>
    <p>Loading...</p>
  </div>
} @else {
  <div class="workflow-form-container">
    <mat-sidenav-container class="sidenav-container">
      <mat-sidenav #actionsSidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
          [title]="isEditMode ? 'Edit Request' : 'New Request'"
          [actions]="sidenavActions">
        </app-entity-actions-sidenav>
      </mat-sidenav>

      <mat-sidenav-content class="form-content">
        <!-- The "Workflow intake is in preview" banner was removed: the workflow API is
             deployed and the org table now exists, so it was telling paying customers
             the feature was unfinished. The file-received line is kept, since that is
             real feedback about what arrived from the intake dialog. -->
        @if (fromWorkflowRequest) {
          <div class="workflow-preview-banner">
            <mat-icon>info_outline</mat-icon>
            <span>File received: <strong>{{ prefilledFileName }}</strong></span>
          </div>
        }

        <div class="header-bar">
          <h2>{{ isEditMode ? 'Edit Workflow Request' : 'New Workflow Request' }}</h2>
        </div>

        <form [formGroup]="form">
          <!-- Request Details -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>Request Details</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="form-grid">
                <mat-form-field appearance="outline">
                  <mat-label>Request Name</mat-label>
                  <input matInput formControlName="name">
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Document Type</mat-label>
                  <mat-select formControlName="documentType">
                    @for (type of documentTypes; track type) {
                      <mat-option [value]="type">{{ type }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Transaction Type</mat-label>
                  <mat-select formControlName="transactionType">
                    @for (type of transactionTypes; track type) {
                      <mat-option [value]="type">{{ type }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <!-- Phase is create-only. The backend enforces ±1 transitions and returns
                     400 "Cannot skip phases", so an unconstrained 6-item dropdown in edit
                     mode could only ever produce errors. Phase moves belong to the detail
                     view's Advance / Return actions, which already step one at a time. -->
                @if (!isEditMode) {
                  <mat-form-field appearance="outline">
                    <mat-label>Phase</mat-label>
                    <mat-select formControlName="phase">
                      @for (phase of phases; track phase) {
                        <mat-option [value]="phase">{{ phase }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else {
                  <mat-form-field appearance="outline">
                    <mat-label>Phase</mat-label>
                    <input matInput [value]="existingRequest?.phase || ''" disabled>
                    <mat-hint>Use Advance / Return on the request to change phase</mat-hint>
                  </mat-form-field>
                }

                <mat-form-field appearance="outline">
                  <mat-label>Assigned To</mat-label>
                  <input matInput
                         type="text"
                         formControlName="assignedTo"
                         [matAutocomplete]="assigneeAuto"
                         (input)="filterUsers($event)"
                         placeholder="Search for a user">
                  <mat-icon matSuffix>person</mat-icon>
                  <mat-autocomplete #assigneeAuto="matAutocomplete" [displayWith]="displayUserFn">
                    @for (user of filteredUsers; track user.email) {
                      <mat-option [value]="user">{{ user.shortFriendlyName }}</mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Status</mat-label>
                  <mat-select formControlName="status">
                    @for (status of statuses; track status) {
                      <mat-option [value]="status">{{ status }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Priority</mat-label>
                  <mat-select formControlName="priority">
                    @for (priority of priorities; track priority) {
                      <mat-option [value]="priority">{{ priority }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Date Requested</mat-label>
                  <input matInput [matDatepicker]="picker" formControlName="dateRequested">
                  <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                  <mat-datepicker #picker></mat-datepicker>
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Source Entity -->
          @if (sourceEntityType || sourceEntityId) {
            <mat-card class="form-card">
              <mat-card-header>
                <mat-card-title>Source Entity</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="source-info">
                  @if (sourceEntityType) {
                    <div class="info-item">
                      <span class="label">Entity Type</span>
                      <span class="value">{{ sourceEntityType }}</span>
                    </div>
                  }
                  @if (sourceRecord) {
                    <div class="info-item">
                      <span class="label">Source Record</span>
                      <span class="value">{{ sourceRecord }}</span>
                    </div>
                  }
                </div>
              </mat-card-content>
            </mat-card>
          }

          <!-- Notes -->
          <mat-card class="form-card">
            <mat-card-header>
              <mat-card-title>Request Notes</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Notes</mat-label>
                <textarea matInput formControlName="requestNotes" rows="4"></textarea>
              </mat-form-field>
            </mat-card-content>
          </mat-card>
        </form>

        @if (saving) {
          <div class="saving-overlay">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Saving...</p>
          </div>
        }
      </mat-sidenav-content>
    </mat-sidenav-container>
  </div>
}
